import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/cn';
import { useEffect, useState } from 'react';

interface Props {
  open: boolean;
  /** 현재 cron (편집 시 prefill). null = 수동. */
  initial: string | null;
  onClose: () => void;
  onSave: (cron: string | null) => void;
}

type Mode = 'manual' | 'daily' | 'hourly' | 'weekly' | 'raw';

const WEEKDAYS = [
  { value: 0, label: '일' },
  { value: 1, label: '월' },
  { value: 2, label: '화' },
  { value: 3, label: '수' },
  { value: 4, label: '목' },
  { value: 5, label: '금' },
  { value: 6, label: '토' },
] as const;

function parseInitial(cron: string | null): {
  mode: Mode;
  hh: string;
  mm: string;
  dow: number;
  raw: string;
} {
  if (!cron) return { mode: 'manual', hh: '01', mm: '03', dow: 1, raw: '' };
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return { mode: 'raw', hh: '01', mm: '03', dow: 1, raw: cron };
  const [m, h, dom, mon, dow] = parts as [string, string, string, string, string];
  // 매일 HH:MM
  if (dom === '*' && mon === '*' && dow === '*' && /^\d+$/.test(m) && /^\d+$/.test(h)) {
    return { mode: 'daily', hh: h.padStart(2, '0'), mm: m.padStart(2, '0'), dow: 1, raw: cron };
  }
  // 매시간 :MM
  if (dom === '*' && mon === '*' && dow === '*' && h === '*' && /^\d+$/.test(m)) {
    return { mode: 'hourly', hh: '00', mm: m.padStart(2, '0'), dow: 1, raw: cron };
  }
  // 매주 요일 HH:MM
  if (dom === '*' && mon === '*' && /^\d+$/.test(m) && /^\d+$/.test(h) && /^\d+$/.test(dow)) {
    return {
      mode: 'weekly',
      hh: h.padStart(2, '0'),
      mm: m.padStart(2, '0'),
      dow: Number(dow),
      raw: cron,
    };
  }
  return { mode: 'raw', hh: '01', mm: '03', dow: 1, raw: cron };
}

export function TableauScheduleDialog({ open, initial, onClose, onSave }: Props) {
  const [state, setState] = useState(parseInitial(initial));
  useEffect(() => {
    if (open) setState(parseInitial(initial));
  }, [open, initial]);

  if (!open) return null;

  const buildCron = (): string | null => {
    switch (state.mode) {
      case 'manual':
        return null;
      case 'daily':
        return `${Number(state.mm)} ${Number(state.hh)} * * *`;
      case 'hourly':
        return `${Number(state.mm)} * * * *`;
      case 'weekly':
        return `${Number(state.mm)} ${Number(state.hh)} * * ${state.dow}`;
      case 'raw':
        return state.raw.trim() || null;
    }
  };

  const save = () => {
    const cron = buildCron();
    if (cron && !/^(\S+\s+){4}\S+$/.test(cron.trim())) {
      window.alert('5필드 cron 식이 아닙니다 (분 시 일 월 요일).');
      return;
    }
    onSave(cron);
    onClose();
  };

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: backdrop 클릭은 닫기 보조 — Esc 키는 안쪽 div 에서 처리
    // biome-ignore lint/a11y/useSemanticElements: <dialog> 미사용 (showModal API 가 portal 흐름과 충돌)
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-border bg-surface p-5 shadow-lg"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
        }}
      >
        <h3 className="mb-4 text-base font-medium">스케줄 설정</h3>

        <Field label="모드" htmlFor="t-sched-mode">
          <div className="flex flex-wrap gap-2">
            {(
              [
                { v: 'manual', label: '수동만' },
                { v: 'daily', label: '매일' },
                { v: 'hourly', label: '매시간' },
                { v: 'weekly', label: '매주' },
                { v: 'raw', label: 'cron 직접' },
              ] as const
            ).map((opt) => (
              <button
                type="button"
                key={opt.v}
                onClick={() => setState((s) => ({ ...s, mode: opt.v }))}
                className={cn(
                  'h-8 px-3 rounded-md border text-xs',
                  state.mode === opt.v
                    ? 'border-accent bg-accent/10 text-foreground'
                    : 'border-border text-foreground-muted hover:text-foreground',
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </Field>

        {state.mode === 'daily' || state.mode === 'weekly' ? (
          <div className="mt-4 grid grid-cols-2 gap-3">
            <Field label="시" htmlFor="t-sched-h">
              <Input
                id="t-sched-h"
                type="number"
                min={0}
                max={23}
                value={state.hh}
                onChange={(e) => setState((s) => ({ ...s, hh: e.target.value }))}
              />
            </Field>
            <Field label="분" htmlFor="t-sched-m">
              <Input
                id="t-sched-m"
                type="number"
                min={0}
                max={59}
                value={state.mm}
                onChange={(e) => setState((s) => ({ ...s, mm: e.target.value }))}
              />
            </Field>
          </div>
        ) : null}

        {state.mode === 'hourly' ? (
          <div className="mt-4">
            <Field label="분 (매시간 :MM)" htmlFor="t-sched-mm">
              <Input
                id="t-sched-mm"
                type="number"
                min={0}
                max={59}
                value={state.mm}
                onChange={(e) => setState((s) => ({ ...s, mm: e.target.value }))}
              />
            </Field>
          </div>
        ) : null}

        {state.mode === 'weekly' ? (
          <div className="mt-4">
            <Field label="요일" htmlFor="t-sched-dow">
              <div className="flex gap-1">
                {WEEKDAYS.map((d) => (
                  <button
                    type="button"
                    key={d.value}
                    onClick={() => setState((s) => ({ ...s, dow: d.value }))}
                    className={cn(
                      'h-8 w-9 rounded-md border text-xs',
                      state.dow === d.value
                        ? 'border-accent bg-accent/10 text-foreground'
                        : 'border-border text-foreground-muted hover:text-foreground',
                    )}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </Field>
          </div>
        ) : null}

        {state.mode === 'raw' ? (
          <div className="mt-4">
            <Field label="cron 식 (분 시 일 월 요일)" htmlFor="t-sched-raw">
              <Input
                id="t-sched-raw"
                value={state.raw}
                onChange={(e) => setState((s) => ({ ...s, raw: e.target.value }))}
                placeholder="예: 3 1 * * *  (매일 01:03)"
              />
            </Field>
          </div>
        ) : null}

        <p className="mt-4 text-xs text-foreground-muted">
          현재 설정: <span className="font-mono">{buildCron() ?? '수동만 (자동 실행 없음)'}</span>
        </p>

        <div className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            취소
          </Button>
          <Button type="button" onClick={save}>
            저장
          </Button>
        </div>
      </div>
    </div>
  );
}
